import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { io } from 'socket.io-client';
import toast from 'react-hot-toast';
import {
  HiOutlineRefresh, HiOutlineSearch, HiOutlineX, HiOutlineShoppingCart,
  HiOutlineClipboardList, HiOutlineCog, HiOutlineLightningBolt
} from 'react-icons/hi';
import { processTrackerAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import DashboardLayout from '../../components/layout/DashboardLayout';

const SOCKET_URL = process.env.REACT_APP_API_URL || (window.location.port ? `${window.location.protocol}//${window.location.hostname}:9898` : window.location.origin);
const POLL_MS = 30000;

const TABS = [
  { key: 'orders', label: 'Orders', icon: <HiOutlineShoppingCart />, hint: 'Enquiry → Sales Order → Approval → Store → QC → Packing → Dispatch → Gate → Delivery → Payment' },
  { key: 'purchases', label: 'Purchases', icon: <HiOutlineClipboardList />, hint: 'Vendor Quotation → PO → Gate Inward → Verification → GRN → Store' },
  { key: 'production', label: 'Production', icon: <HiOutlineCog />, hint: 'Build Job → Production → QC → Store' },
  { key: 'activity', label: 'Live Activity', icon: <HiOutlineLightningBolt />, hint: 'Every action by every user, as it happens' }
];

const FETCHERS = {
  orders: processTrackerAPI.getOrders,
  purchases: processTrackerAPI.getPurchases,
  production: processTrackerAPI.getProduction
};

const STATE_STYLE = {
  done: { color: '#16a34a', bg: '#16a34a', label: 'Done' },
  current: { color: '#2563eb', bg: '#2563eb', label: 'In progress' },
  blocked: { color: '#dc2626', bg: '#dc2626', label: 'Blocked' },
  pending: { color: '#cbd5e1', bg: 'transparent', label: 'Not started' },
  skipped: { color: '#e2e8f0', bg: '#e2e8f0', label: 'Skipped' }
};

const STUCK_HOURS = 24;
const CRITICAL_HOURS = 72;

const fmtDateTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true }) : '');
const fmtDuration = (hours) => {
  if (hours === null || hours === undefined) return '';
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m`;
  if (hours < 48) return `${Math.round(hours)}h`;
  const days = Math.floor(hours / 24);
  const rem = Math.round(hours - days * 24);
  return rem ? `${days}d ${rem}h` : `${days}d`;
};
const hoursBetween = (a, b) => (a && b ? (new Date(b) - new Date(a)) / 36e5 : null);
const fmtMoney = (n) => (n ? `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}` : '');
const timeAgo = (d) => {
  const s = Math.round((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 10) return 'just now';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return fmtDuration(s / 3600) + ' ago';
};

const waitTone = (j) => {
  if (j.completed) return { color: '#16a34a', bg: 'rgba(22,163,74,0.1)' };
  if (j.current.state === 'blocked') return { color: '#dc2626', bg: 'rgba(220,38,38,0.1)' };
  if (j.current.waitingHours >= CRITICAL_HOURS) return { color: '#dc2626', bg: 'rgba(220,38,38,0.1)' };
  if (j.current.waitingHours >= STUCK_HOURS) return { color: '#d97706', bg: 'rgba(217,119,6,0.12)' };
  return { color: '#2563eb', bg: 'rgba(37,99,235,0.08)' };
};

// Compact dot stepper; hover a dot for its details
function Stepper({ steps }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', minWidth: '220px' }}>
      {steps.map((s, i) => {
        const st = STATE_STYLE[s.state] || STATE_STYLE.pending;
        const isCur = s.state === 'current' || (s.state === 'blocked');
        const size = isCur ? 14 : 10;
        const tip = [s.label, st.label, s.at && fmtDateTime(s.at), s.by && `by ${s.by}`, s.note].filter(Boolean).join('\n');
        return (
          <React.Fragment key={s.key}>
            {i > 0 && <div style={{ flex: 1, height: '2px', minWidth: '6px', background: s.state === 'done' || s.state === 'current' || s.state === 'blocked' ? '#86efac' : 'var(--border)' }} />}
            <div title={tip} style={{
              width: size, height: size, borderRadius: '50%', flexShrink: 0,
              background: st.bg, border: `2px solid ${st.color}`,
              boxShadow: isCur ? `0 0 0 3px ${st.color}33` : 'none'
            }} />
          </React.Fragment>
        );
      })}
    </div>
  );
}

function JourneyDetail({ journey, kind, refreshKey, onClose }) {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(kind === 'orders');

  // Re-read the history whenever the tracker refreshes, so an open panel stays live
  useEffect(() => {
    if (kind !== 'orders') return;
    let alive = true;
    processTrackerAPI.getOrderHistory(journey.id)
      .then(res => { if (alive) setHistory(res.data.data); })
      .catch(() => toast.error('Could not load full history'))
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [journey.id, kind, refreshKey]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const j = history?.journey || journey;
  const tone = waitTone(j);
  const doneSteps = j.steps.filter(s => s.state === 'done').length;
  const countable = j.steps.filter(s => s.state !== 'skipped').length;

  return (
    <div onClick={e => e.target === e.currentTarget && onClose()}
      style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
      <div style={{ background: 'var(--bg-primary)', width: '100%', maxWidth: '1100px', maxHeight: '92vh', borderRadius: '16px', overflow: 'hidden', display: 'flex', flexDirection: 'column', border: '1px solid var(--border)', boxShadow: '0 25px 60px rgba(15,23,42,0.35)' }}>
        <div style={{ padding: '18px 22px', background: 'var(--bg-sidebar)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: '0.75rem', color: 'var(--text-dim)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{TABS.find(t => t.key === kind)?.label} journey</div>
            <h3 style={{ margin: '2px 0 0', fontSize: '1.2rem', fontWeight: 800, color: 'var(--text-primary)' }}>{j.ref} · {j.title}</h3>
            {j.subtitle && <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginTop: '2px' }}>{j.subtitle}{j.amount ? ` · ${fmtMoney(j.amount)}` : ''}</div>}
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
              <span style={{ background: tone.bg, color: tone.color, border: `1px solid ${tone.color}44`, padding: '3px 10px', borderRadius: '999px', fontSize: '0.76rem', fontWeight: 800 }}>
                {j.completed ? '✅ Completed' : `${j.current.state === 'blocked' ? '⛔ Blocked at' : '📍 Now at'}: ${j.current.label}`}
              </span>
              {!j.completed && (
                <span style={{ background: 'var(--bg-card-hover)', color: 'var(--text-secondary)', padding: '3px 10px', borderRadius: '999px', fontSize: '0.76rem', fontWeight: 700 }}>
                  ⏱ Waiting {fmtDuration(j.current.waitingHours)} · Next: {j.current.teamLabel}
                </span>
              )}
              <span style={{ background: 'var(--bg-card-hover)', color: 'var(--text-secondary)', padding: '3px 10px', borderRadius: '999px', fontSize: '0.76rem', fontWeight: 700 }}>
                {doneSteps}/{countable} steps done
              </span>
            </div>
          </div>
          <button onClick={onClose} title="Close (Esc)" style={{ background: 'none', border: 'none', fontSize: '1.4rem', color: 'var(--text-dim)', cursor: 'pointer', alignSelf: 'flex-start' }}><HiOutlineX /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 440px), 1fr))', gap: '18px', alignItems: 'start' }}>
          {/* Stage-by-stage timeline */}
          <div style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px 18px' }}>
            <div style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px' }}>🧭 Stage by stage</div>
            {j.steps.map((s, i) => {
              const st = STATE_STYLE[s.state] || STATE_STYLE.pending;
              const prevAt = [...j.steps.slice(0, i)].reverse().find(p => p.at)?.at;
              const took = s.state === 'done' && s.at ? hoursBetween(prevAt, s.at) : null;
              const isCur = s.state === 'current' || s.state === 'blocked';
              return (
                <div key={s.key} style={{ display: 'flex', gap: '12px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                    <div style={{ width: 22, height: 22, borderRadius: '50%', border: `2px solid ${st.color}`, background: s.state === 'pending' ? 'var(--bg-sidebar)' : st.bg, color: '#fff', fontSize: '0.7rem', fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, boxShadow: isCur ? `0 0 0 4px ${st.color}26` : 'none' }}>
                      {s.state === 'done' ? '✓' : s.state === 'blocked' ? '!' : s.state === 'skipped' ? '–' : s.state === 'current' ? '•' : ''}
                    </div>
                    {i < j.steps.length - 1 && <div style={{ width: 2, flex: 1, minHeight: 18, background: s.state === 'done' ? '#86efac' : 'var(--border)' }} />}
                  </div>
                  <div style={{ paddingBottom: '14px', flex: 1, minWidth: 0, opacity: s.state === 'skipped' ? 0.55 : 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 800, color: isCur ? st.color : 'var(--text-primary)', fontSize: '0.9rem' }}>{s.label}</span>
                      {took !== null && took >= 0 && <span style={{ fontSize: '0.72rem', color: 'var(--text-dim)', fontWeight: 600 }}>took {fmtDuration(took)}</span>}
                    </div>
                    {s.state === 'done' && (
                      <div style={{ fontSize: '0.78rem', color: 'var(--text-secondary)', marginTop: '2px' }}>
                        {s.at ? `🕒 ${fmtDateTime(s.at)}` : '🕒 time not recorded'}{s.by ? ` · 👤 ${s.by}` : ''}
                      </div>
                    )}
                    {s.state === 'skipped' && <div style={{ fontSize: '0.76rem', color: 'var(--text-dim)' }}>Skipped — record started at a later stage</div>}
                    {isCur && (
                      <div style={{ fontSize: '0.78rem', color: st.color, fontWeight: 700, marginTop: '2px' }}>
                        {s.state === 'blocked' ? '⛔ Blocked' : '⏳ Waiting'} {fmtDuration(j.current.waitingHours)} · Next: {j.current.teamLabel}
                        {j.current.owners.length > 0 && <span style={{ fontWeight: 600, color: 'var(--text-secondary)' }}> ({j.current.owners.join(', ')})</span>}
                      </div>
                    )}
                    {s.state === 'blocked' && s.at && (
                      <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)' }}>🕒 {fmtDateTime(s.at)}{s.by ? ` · 👤 ${s.by}` : ''}</div>
                    )}
                    {s.note && <div style={{ fontSize: '0.76rem', color: 'var(--text-dim)', marginTop: '2px', whiteSpace: 'pre-wrap' }}>{s.note}</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Every recorded action on this order */}
          {kind === 'orders' && (
            <div style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px 18px' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '12px' }}>📜 Complete activity record</div>
              {loading ? (
                <div style={{ color: 'var(--text-dim)', padding: '20px 0' }}>Loading history…</div>
              ) : !history?.events?.length ? (
                <div style={{ color: 'var(--text-dim)', padding: '20px 0' }}>No recorded actions yet.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {[...history.events].reverse().map((e, i) => (
                    <div key={i} style={{ borderLeft: '3px solid var(--primary)', padding: '6px 10px', background: 'var(--bg-card-hover)', borderRadius: '0 8px 8px 0' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', fontSize: '0.74rem' }}>
                        <span style={{ fontWeight: 800, color: 'var(--primary)', textTransform: 'capitalize' }}>{e.source} · {e.action}</span>
                        <span style={{ color: 'var(--text-dim)' }}>{fmtDateTime(e.at)}</span>
                      </div>
                      {e.by && <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '2px' }}>👤 {e.by}{e.role ? <span style={{ color: 'var(--text-dim)', fontWeight: 600 }}> ({String(e.role).replace(/_/g, ' ')})</span> : ''}</div>}
                      {e.detail && <div style={{ fontSize: '0.76rem', color: 'var(--text-secondary)', marginTop: '2px', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{e.detail}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: '12px 22px', borderTop: '1px solid var(--border)', background: 'var(--bg-sidebar)', display: 'flex', justifyContent: 'flex-end' }}>
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

function JourneyBoard({ kind, journeys, loading, onOpen }) {
  const [search, setSearch] = useState('');
  const [show, setShow] = useState('active');
  const [stepFilter, setStepFilter] = useState('');
  const [teamFilter, setTeamFilter] = useState('');

  useEffect(() => { setStepFilter(''); setTeamFilter(''); setSearch(''); setShow('active'); }, [kind]);

  // "Where is everything right now": one card per step, in pipeline order
  const stepCards = useMemo(() => {
    const order = [];
    journeys.forEach(j => j.steps.forEach(s => { if (!order.some(o => o.key === s.key)) order.push({ key: s.key, label: s.label }); }));
    return order.map(o => ({ ...o, count: journeys.filter(j => !j.completed && j.current.key === o.key).length })).filter(c => c.count > 0);
  }, [journeys]);

  // Which team is holding how much work
  const teamLoad = useMemo(() => {
    const map = {};
    journeys.filter(j => !j.completed).forEach(j => {
      const t = j.current.teamLabel || 'Unassigned';
      map[t] = map[t] || { team: t, count: 0, stuck: 0, owners: j.current.owners };
      map[t].count += 1;
      if (j.current.waitingHours >= STUCK_HOURS || j.current.state === 'blocked') map[t].stuck += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [journeys]);

  const totals = useMemo(() => ({
    active: journeys.filter(j => !j.completed).length,
    stuck: journeys.filter(j => !j.completed && j.current.waitingHours >= STUCK_HOURS && j.current.state !== 'blocked').length,
    blocked: journeys.filter(j => !j.completed && j.current.state === 'blocked').length,
    completed: journeys.filter(j => j.completed).length
  }), [journeys]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return journeys.filter(j => {
      if (show === 'active' && j.completed) return false;
      if (show === 'completed' && !j.completed) return false;
      if (show === 'stuck' && (j.completed || j.current.state === 'blocked' || j.current.waitingHours < STUCK_HOURS)) return false;
      if (show === 'blocked' && (j.completed || j.current.state !== 'blocked')) return false;
      if (stepFilter && j.current.key !== stepFilter) return false;
      if (teamFilter && (j.current.teamLabel || 'Unassigned') !== teamFilter) return false;
      if (q && ![j.ref, j.title, j.subtitle, j.salesPerson, j.current.label].some(v => v && String(v).toLowerCase().includes(q))) return false;
      return true;
    }).sort((a, b) => {
      // Most urgent first: blocked, then longest waiting
      if (a.completed !== b.completed) return a.completed ? 1 : -1;
      if ((a.current.state === 'blocked') !== (b.current.state === 'blocked')) return a.current.state === 'blocked' ? -1 : 1;
      return (b.current.waitingHours || 0) - (a.current.waitingHours || 0);
    });
  }, [journeys, search, show, stepFilter, teamFilter]);

  const card = (active, color) => ({
    background: active ? `${color}14` : 'var(--bg-card)', border: `1px solid ${active ? color : 'var(--border)'}`,
    borderRadius: '12px', padding: '12px 14px', cursor: 'pointer', textAlign: 'left', transition: 'all 0.15s'
  });

  return (
    <>
      {/* Headline numbers */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '12px', marginBottom: '14px' }}>
        {[
          { key: 'active', label: 'In progress', value: totals.active, color: '#2563eb' },
          { key: 'stuck', label: `Waiting > ${STUCK_HOURS}h`, value: totals.stuck, color: '#d97706' },
          { key: 'blocked', label: 'Blocked / Rejected', value: totals.blocked, color: '#dc2626' },
          { key: 'completed', label: 'Completed', value: totals.completed, color: '#16a34a' }
        ].map(c => (
          <button key={c.key} type="button" onClick={() => { setShow(show === c.key ? 'all' : c.key); setStepFilter(''); setTeamFilter(''); }} style={card(show === c.key, c.color)}>
            <div style={{ fontSize: '1.7rem', fontWeight: 900, color: c.color, lineHeight: 1.1 }}>{c.value}</div>
            <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-dim)' }}>{c.label}</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 380px), 1fr))', gap: '14px', marginBottom: '16px' }}>
        {/* Where things are now */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '14px' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '10px' }}>📍 Where everything is right now</div>
          {stepCards.length === 0 ? <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>Nothing in progress.</div> : (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {stepCards.map(c => (
                <button key={c.key} type="button" onClick={() => { setStepFilter(stepFilter === c.key ? '' : c.key); setTeamFilter(''); setShow('active'); }}
                  style={{ ...card(stepFilter === c.key, '#2563eb'), padding: '6px 12px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontWeight: 900, color: '#2563eb' }}>{c.count}</span>
                  <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)' }}>{c.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Whose desk the work is on */}
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '14px' }}>
          <div style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', textTransform: 'uppercase', marginBottom: '10px' }}>👥 Waiting on which team</div>
          {teamLoad.length === 0 ? <div style={{ color: 'var(--text-dim)', fontSize: '0.85rem' }}>No pending work.</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {teamLoad.map(t => {
                const max = teamLoad[0].count;
                return (
                  <button key={t.team} type="button" onClick={() => { setTeamFilter(teamFilter === t.team ? '' : t.team); setStepFilter(''); setShow('active'); }}
                    title={t.owners.length ? `People: ${t.owners.join(', ')}` : 'No active user has this role'}
                    style={{ background: teamFilter === t.team ? 'rgba(37,99,235,0.08)' : 'transparent', border: 'none', padding: '4px 6px', borderRadius: '8px', cursor: 'pointer', display: 'grid', gridTemplateColumns: '130px 1fr auto', alignItems: 'center', gap: '10px', textAlign: 'left' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.team}</span>
                    <span style={{ height: '8px', borderRadius: '4px', background: 'var(--border-light)', overflow: 'hidden' }}>
                      <span style={{ display: 'block', height: '100%', width: `${(t.count / max) * 100}%`, background: t.stuck ? '#d97706' : '#2563eb', borderRadius: '4px' }} />
                    </span>
                    <span style={{ fontSize: '0.78rem', fontWeight: 800, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {t.count}{t.stuck ? <span style={{ color: '#d97706' }}> · {t.stuck} late</span> : ''}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Search and filters */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
          <HiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
          <input className="form-input" style={{ paddingLeft: '36px' }} placeholder="Search number, customer / vendor, product, salesperson…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" style={{ width: '190px' }} value={show} onChange={e => setShow(e.target.value)}>
          <option value="active">In progress</option>
          <option value="stuck">Waiting &gt; {STUCK_HOURS}h</option>
          <option value="blocked">Blocked / Rejected</option>
          <option value="completed">Completed</option>
          <option value="all">All</option>
        </select>
        {(stepFilter || teamFilter) && (
          <button type="button" className="btn btn-secondary" onClick={() => { setStepFilter(''); setTeamFilter(''); }}>
            Clear: {stepFilter ? stepCards.find(c => c.key === stepFilter)?.label : teamFilter} ✕
          </button>
        )}
      </div>

      {/* Journeys */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: '980px', borderCollapse: 'collapse', fontSize: '0.86rem' }}>
            <thead>
              <tr style={{ background: 'var(--bg-card-hover)' }}>
                {['Number', kind === 'purchases' ? 'Vendor' : kind === 'production' ? 'Product' : 'Customer', 'Progress', 'Now at', 'Waiting', 'Next action by', 'Last update'].map(h => (
                  <th key={h} style={{ padding: '11px 14px', textAlign: 'left', fontSize: '0.73rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && journeys.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-dim)' }}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-dim)' }}>Nothing matches these filters.</td></tr>
              ) : filtered.map(j => {
                const tone = waitTone(j);
                const done = j.steps.filter(s => s.state === 'done').length;
                const countable = j.steps.filter(s => s.state !== 'skipped').length;
                return (
                  <tr key={j.id} onClick={() => onOpen(j)} style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer' }}
                    onMouseEnter={e => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: 800, color: 'var(--primary)', fontFamily: 'ui-monospace, Consolas, monospace' }}>{j.ref}</div>
                      {j.amount > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)' }}>{fmtMoney(j.amount)}</div>}
                    </td>
                    <td style={{ padding: '11px 14px', maxWidth: '220px' }}>
                      <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{j.title}</div>
                      {j.subtitle && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{j.subtitle}</div>}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <Stepper steps={j.steps} />
                      <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '4px' }}>{done}/{countable} steps</div>
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      <div style={{ fontWeight: 800, color: j.completed ? '#16a34a' : j.current.state === 'blocked' ? '#dc2626' : 'var(--text-primary)' }}>
                        {j.completed ? '✅ Completed' : `${j.current.state === 'blocked' ? '⛔ ' : ''}${j.current.label}`}
                      </div>
                      {j.current.note && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', maxWidth: '220px' }}>{j.current.note}</div>}
                    </td>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap' }}>
                      {!j.completed && (
                        <span style={{ background: tone.bg, color: tone.color, padding: '3px 9px', borderRadius: '999px', fontSize: '0.76rem', fontWeight: 800 }}>
                          {fmtDuration(j.current.waitingHours)}
                        </span>
                      )}
                      {!j.completed && <div style={{ fontSize: '0.7rem', color: 'var(--text-dim)', marginTop: '3px' }}>since {fmtDateTime(j.current.since)}</div>}
                    </td>
                    <td style={{ padding: '11px 14px' }}>
                      {j.completed ? <span style={{ color: 'var(--text-dim)' }}>—</span> : (
                        <>
                          <div style={{ fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{j.current.teamLabel}</div>
                          <div style={{ fontSize: '0.72rem', color: j.current.owners.length ? 'var(--text-dim)' : '#dc2626', maxWidth: '200px' }}>
                            {j.current.owners.length ? j.current.owners.slice(0, 3).join(', ') + (j.current.owners.length > 3 ? ` +${j.current.owners.length - 3}` : '') : 'No active user for this role'}
                          </div>
                        </>
                      )}
                    </td>
                    <td style={{ padding: '11px 14px', whiteSpace: 'nowrap', fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{fmtDateTime(j.updatedAt)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)', marginTop: '8px' }}>
        Showing {filtered.length} of {journeys.length}. Most urgent first: blocked, then longest waiting. Click any row for its full stage-by-stage history.
      </div>
    </>
  );
}

function ActivityFeed({ items, loading, freshIds }) {
  const [module, setModule] = useState('');
  const [search, setSearch] = useState('');
  const modules = useMemo(() => Array.from(new Set(items.map(i => i.module).filter(Boolean))).sort(), [items]);
  const filtered = items.filter(i => (!module || i.module === module)
    && (!search || [i.message, i.actorName, i.record, i.changes].some(v => v && v.toLowerCase().includes(search.toLowerCase()))));

  // Group under day headings
  const groups = [];
  filtered.forEach(i => {
    const day = new Date(i.at).toLocaleDateString('en-IN', { weekday: 'long', day: '2-digit', month: 'short', year: 'numeric' });
    const g = groups[groups.length - 1];
    if (g && g.day === day) g.items.push(i); else groups.push({ day, items: [i] });
  });

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: '220px' }}>
          <HiOutlineSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--text-dim)' }} />
          <input className="form-input" style={{ paddingLeft: '36px' }} placeholder="Search person, record, action…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-input" style={{ width: '200px' }} value={module} onChange={e => setModule(e.target.value)}>
          <option value="">All departments</option>
          {modules.map(m => <option key={m} value={m}>{m}</option>)}
        </select>
      </div>
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '6px 16px 14px' }}>
        {loading && items.length === 0 ? <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-dim)' }}>Loading…</div>
          : groups.length === 0 ? <div style={{ padding: '30px', textAlign: 'center', color: 'var(--text-dim)' }}>No activity yet.</div>
            : groups.map(g => (
              <div key={g.day}>
                <div style={{ fontSize: '0.74rem', fontWeight: 800, color: 'var(--text-dim)', textTransform: 'uppercase', padding: '12px 0 6px' }}>{g.day}</div>
                {g.items.map(i => (
                  <div key={i.id} style={{
                    display: 'grid', gridTemplateColumns: '78px 1fr', gap: '12px', padding: '8px 10px', borderRadius: '8px',
                    background: freshIds.has(i.id) ? 'rgba(37,99,235,0.08)' : 'transparent', transition: 'background 1.5s'
                  }}>
                    <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', whiteSpace: 'nowrap' }}>
                      {new Date(i.at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.68rem', fontWeight: 800, padding: '1px 8px', borderRadius: '999px', background: 'rgba(249,115,22,0.12)', color: 'var(--primary)' }}>{i.module || 'General'}</span>
                        <span style={{ fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 600 }}>{i.message}</span>
                      </div>
                      {i.changes && <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', marginTop: '2px', wordBreak: 'break-word' }}>{i.changes}</div>}
                    </div>
                  </div>
                ))}
              </div>
            ))}
      </div>
    </>
  );
}

export default function ProcessTracker() {
  const { user } = useAuth();
  const [tab, setTab] = useState('orders');
  const [days, setDays] = useState('90');
  const [data, setData] = useState({ orders: [], purchases: [], production: [] });
  const [activity, setActivity] = useState([]);
  const [freshIds, setFreshIds] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [live, setLive] = useState(false);
  const [, setTick] = useState(0);
  const [selected, setSelected] = useState(null);
  const tabRef = useRef(tab);
  const knownIds = useRef(new Set());
  tabRef.current = tab;

  const loadActivity = useCallback(async () => {
    const res = await processTrackerAPI.getActivity({ limit: 300 });
    const rows = res.data.data || [];
    // Highlight entries that arrived since the last load
    if (knownIds.current.size) {
      const fresh = new Set(rows.filter(r => !knownIds.current.has(r.id)).map(r => r.id));
      if (fresh.size) {
        setFreshIds(fresh);
        setTimeout(() => setFreshIds(new Set()), 4000);
      }
    }
    knownIds.current = new Set(rows.map(r => r.id));
    setActivity(rows);
  }, []);

  const load = useCallback(async (which = tabRef.current) => {
    try {
      if (which === 'activity') await loadActivity();
      else {
        const res = await FETCHERS[which]({ days: days === 'all' ? 0 : days });
        setData(prev => ({ ...prev, [which]: res.data.data || [] }));
      }
      setLastUpdated(new Date());
    } catch (err) {
      toast.error(err.response?.data?.message || 'Could not load the tracker');
    } finally {
      setLoading(false);
    }
  }, [days, loadActivity]);

  useEffect(() => { setLoading(true); load(tab); }, [tab, load]);

  // Live updates: the server pings on every user action; polling covers a dropped socket
  useEffect(() => {
    let timer;
    const refresh = () => {
      clearTimeout(timer);
      timer = setTimeout(() => load(tabRef.current), 700);
    };
    const socket = io(SOCKET_URL, { withCredentials: true });
    socket.on('connect', () => {
      setLive(true);
      const orgId = user?.organizationId?._id || user?.organizationId;
      const isPlatformAdmin = user?.role === 'super_admin' && user?.email === process.env.REACT_APP_PLATFORM_ADMIN_EMAIL;
      if (isPlatformAdmin) socket.emit('join_superadmin');
      if (orgId) {
        socket.emit('join_role_room', { orgId, role: 'admin' });
        socket.emit('join_role_room', { orgId, role: 'super_admin' });
      }
    });
    socket.on('disconnect', () => setLive(false));
    socket.on('connect_error', () => setLive(false));
    socket.on('process_activity', refresh);

    const poll = setInterval(() => { if (!document.hidden) load(tabRef.current); }, POLL_MS);
    const onVisible = () => { if (!document.hidden) load(tabRef.current); };
    document.addEventListener('visibilitychange', onVisible);
    const tick = setInterval(() => setTick(t => t + 1), 5000);

    return () => {
      clearTimeout(timer);
      clearInterval(poll);
      clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
      socket.disconnect();
    };
  }, [user, load]);

  const current = TABS.find(t => t.key === tab);

  return (
    <DashboardLayout pageTitle="Process Tracker">
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.35rem', fontWeight: 800, color: 'var(--text-primary)' }}>🧭 Process Tracker</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: 'var(--text-dim)' }}>
            Every order, purchase and production job: where it is, since when, who handled each step, and who has to act next.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <span title={live ? 'Updates the moment anyone takes an action' : `Live connection unavailable — refreshing every ${POLL_MS / 1000}s`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', fontWeight: 800, color: live ? '#16a34a' : '#d97706', background: live ? 'rgba(22,163,74,0.1)' : 'rgba(217,119,6,0.1)', padding: '4px 10px', borderRadius: '999px' }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: live ? '#16a34a' : '#d97706', boxShadow: live ? '0 0 0 3px rgba(22,163,74,0.25)' : 'none' }} />
            {live ? 'LIVE' : `Auto-refresh ${POLL_MS / 1000}s`}
          </span>
          {lastUpdated && <span style={{ fontSize: '0.76rem', color: 'var(--text-dim)' }}>Updated {timeAgo(lastUpdated)}</span>}
          {tab !== 'activity' && (
            <select className="form-input" style={{ width: '150px' }} value={days} onChange={e => setDays(e.target.value)} title="Show records updated within">
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last 1 year</option>
              <option value="all">All time</option>
            </select>
          )}
          <button className="btn btn-secondary" onClick={() => { setLoading(true); load(tab); }} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            <HiOutlineRefresh /> Refresh
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '6px', borderBottom: '1px solid var(--border)', marginBottom: '8px', overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t.key} type="button" onClick={() => setTab(t.key)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
              fontWeight: 800, fontSize: '0.88rem', color: tab === t.key ? 'var(--primary)' : 'var(--text-dim)',
              borderBottom: `3px solid ${tab === t.key ? 'var(--primary)' : 'transparent'}`, marginBottom: '-1px'
            }}>
            {t.icon} {t.label}
            {t.key !== 'activity' && data[t.key]?.length > 0 && (
              <span style={{ fontSize: '0.7rem', background: tab === t.key ? 'rgba(249,115,22,0.15)' : 'var(--bg-card-hover)', padding: '1px 7px', borderRadius: '999px' }}>
                {data[t.key].filter(j => !j.completed).length}
              </span>
            )}
          </button>
        ))}
      </div>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginBottom: '16px' }}>{current.hint}</div>

      {tab === 'activity'
        ? <ActivityFeed items={activity} loading={loading} freshIds={freshIds} />
        : <JourneyBoard kind={tab} journeys={data[tab]} loading={loading} onOpen={setSelected} />}

      {selected && (
        <JourneyDetail
          journey={(data[tab] || []).find(x => x.id === selected.id) || selected}
          kind={tab}
          refreshKey={lastUpdated?.getTime()}
          onClose={() => setSelected(null)}
        />
      )}
    </DashboardLayout>
  );
}
